import { DatePickerField } from '@/components/molecules/date-picker-field'
import { DateRangePickerField } from '@/components/molecules/date-range-picker-field'
import { useDesignSystemPage } from './page.design-system.hook'
import {
  H1,
  H2,
  H3,
  H4,
  H5,
  Text,
  TextLg,
  Span,
  Caption,
  ActionButton,
  Button,
  Input,
  Textarea,
  Link,
  LoadingDots,
  BellIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  SaveIcon,
  RadioInput,
  CheckboxInput,
  QuickActionButton,
} from '@/components/atoms'
import * as ArrowIcons from '@/components/atoms/icons/arrow'
import * as CoverageFeatureIcons from '@/components/atoms/icons/coverage-feature'
import * as CalendarIcons from '@/components/atoms/icons/calendar'
import * as CommunicationIcons from '@/components/atoms/icons/communication'
import * as EditIcons from '@/components/atoms/icons/edit'
import * as EnvironmentIcons from '@/components/atoms/icons/environment'
import * as FileIcons from '@/components/atoms/icons/file'
import * as InterfaceIcons from '@/components/atoms/icons/interface'
import * as MediaIcons from '@/components/atoms/icons/media'
import * as MenuIcons from '@/components/atoms/icons/menu'
import * as NavigationIcons from '@/components/atoms/icons/navigation'
import * as ShapeIcons from '@/components/atoms/icons/shape'
import * as SystemIcons from '@/components/atoms/icons/system'
import * as UserIcons from '@/components/atoms/icons/user'
import * as WarningIcons from '@/components/atoms/icons/warning'
import { Toast } from '@/components/molecules/toast'
import { SelectField } from '@/components/molecules/select-field'
import { LinkCard } from '@/components/molecules/link-card'
import { Modal } from '@/components/molecules/modal'
import { ActionModal } from '@/components/molecules/action-modal'
import type { ActionModalVariant } from '@/components/molecules/action-modal'
import { Sidebar } from '@/components/molecules/sidebar'
import { OtpInput } from '@/components/molecules/otp-input'
import { ResendOtp } from '@/components/molecules/resend-otp'
import { PolicyCard } from '@/components/molecules/policy-card'
import { IconTile } from '@/components/molecules/icon-tile'
import { PromoCard } from '@/components/molecules/promo-card'
import { AppDownloadBanner } from '@/components/molecules/app-download-banner'
import { BillingCard } from '@/components/molecules/billing-card'
import { GoalCard } from '@/components/molecules/goal-card'
import { PerkCard } from '@/components/molecules/perk-card'
import { ProfileMenu } from '@/components/molecules/profile-menu'
import { FeatureItem } from '@/components/molecules/feature-item'
import { BenefitCard } from '@/components/molecules/benefit-card'
import { CoverageBanner } from '@/components/molecules/coverage-banner'
import { FileUpload } from '@/components/molecules/file-upload'
import { SelectedFileList } from '@/components/molecules/selected-file-list'
import { SignaturePad } from '@/components/molecules/signature-pad'
import { ClaimCard } from '@/components/molecules/claim-card'
import { IdCard } from '@/components/molecules/id-card'
import { UnderConstruction } from '@/components/molecules/under-construction'
import { Slider } from '@/components/molecules/slider'
import { Stepper } from '@/components/molecules/stepper'
import type { StepperItem } from '@/components/molecules/stepper'
import { Chip } from '@/components/molecules/chip'
import { FilterChips } from '@/components/molecules/filter-chips'
import { SelectableCard } from '@/components/molecules/selectable-card'
import { InfoBanner } from '@/components/molecules/info-banner'
import { EmptySection } from '@/components/molecules/empty-section'
import { LanguageSwitcher } from '@/components/molecules/language-switcher'
import { RepeatableFormSection } from '@/components/molecules/repeatable-form-section'
import {
  goodDog,
  goodCat,
  easyClaims,
  manageYourPolicy,
  getPaidBack,
  agentsMan,
  exclusiveBenefits,
  invoice,
  money,
  downloadDocuments,
  perkTelevet,
  perkDirectVet,
} from '@/shared/assets'

function Section({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <section className="rounded-xl bg-white shadow-sm">
      <div className="rounded-t-xl bg-neutral-100 px-6 py-3">
        <Caption className="text-xs font-bold tracking-widest uppercase">
          {title}
        </Caption>
      </div>
      <div className="space-y-8 p-6">{children}</div>
    </section>
  )
}

function Row({
  title,
  children,
  col,
}: {
  title: string
  children: React.ReactNode
  col?: boolean
}) {
  return (
    <div>
      <Caption className="mb-3 text-xs font-semibold tracking-wider text-neutral-600 uppercase">
        {title}
      </Caption>
      <div
        className={
          col ? 'flex flex-col gap-4' : 'flex flex-wrap items-center gap-6'
        }
      >
        {children}
      </div>
    </div>
  )
}

function Item({
  label,
  children,
  wide,
}: {
  label: string
  children: React.ReactNode
  wide?: boolean
}) {
  return (
    <div
      className={`flex flex-col items-center gap-2 ${wide ? 'w-full max-w-sm' : ''}`}
    >
      <div className={wide ? 'w-full' : ''}>{children}</div>
      <Caption className="text-xs text-neutral-300">{label}</Caption>
    </div>
  )
}

const PET_OPTIONS = [
  { value: 'dog', label: 'Dog' },
  { value: 'cat', label: 'Cat' },
  { value: 'rabbit', label: 'Rabbit' },
  { value: 'hamster', label: 'Hamster' },
]

export function DesignSystemPage() {
  const {
    modalOpen,
    setModalOpen,
    actionModalOpen,
    actionModalVariant,
    actionModalActions,
    openActionModal,
    closeActionModal,
    sidebarOpen,
    setSidebarOpen,
    selectValue,
    setSelectValue,
    searchSelectValue,
    setSearchSelectValue,
    toastVariant,
    setToastVariant,
    stepperStep,
    setStepperStep,
    radioValue,
    setRadioValue,
    checkboxDog,
    setCheckboxDog,
    checkboxCat,
    setCheckboxCat,
    dateValue,
    setDateValue,
    dateRange,
    setDateRange,
    limitedDateValue,
    setLimitedDateValue,
    limitedDateRange,
    setLimitedDateRange,
    dateMin,
    dateMax,
    chipSelected,
    setChipSelected,
    activeFilter,
    setActiveFilter,
    selectableCheckboxA,
    setSelectableCheckboxA,
    selectableCheckboxB,
    setSelectableCheckboxB,
    selectableRadio,
    setSelectableRadio,
    repeatableEntries,
    addRepeatable,
    removeRepeatable,
    updateRepeatable,
    textareaValue,
    setTextareaValue,
    selectedFiles,
    addSelectedFiles,
    removeSelectedFile,
    hasSignature,
    handleSignatureChange,
  } = useDesignSystemPage()

  return (
    <div className="min-h-screen bg-neutral-50 pb-16">
      <div className="bg-gradient-indigo-coral px-6 py-8">
        <H1 className="text-white">Design System</H1>
        <Span className="mt-1 block text-indigo-50">
          Component library &amp; variants
        </Span>
      </div>

      <div className="mx-auto max-w-3xl space-y-6 px-4 pt-8">
        {/* ── Typography ── */}
        <Section title="Atoms · Typography">
          <Row title="Headings" col>
            <Item label="Heading-01 · Merriweather Bold · 80px" wide>
              <H1>Heading 1</H1>
            </Item>
            <Item label="Heading-02 · Atkinson Bold · 50px" wide>
              <H2>Heading 2</H2>
            </Item>
            <Item label="Heading-03 · Atkinson Bold · 32px" wide>
              <H3>Heading 3</H3>
            </Item>
            <Item label="Heading-04 Bold · Atkinson Bold · 24px" wide>
              <H4>Heading 4</H4>
            </Item>
            <Item label="Heading-04 Regular · Atkinson Regular · 24px" wide>
              <H4 bold={false}>Heading 4</H4>
            </Item>
            <Item label="Heading-05 Bold · Atkinson Bold · 20px" wide>
              <H5>Heading 5</H5>
            </Item>
            <Item label="Heading-05 Regular · Atkinson Regular · 20px" wide>
              <H5 bold={false}>Heading 5</H5>
            </Item>
          </Row>
          <Row title="Body" col>
            <Item
              label="body-01 · Atkinson Regular · 16px / 0.16px tracking"
              wide
            >
              <Text>The quick brown fox jumps over the lazy dog</Text>
            </Item>
            <Item
              label="body-02 · Atkinson Regular · 18px / 0.16px tracking"
              wide
            >
              <TextLg>The quick brown fox jumps over the lazy dog</TextLg>
            </Item>
            <Item label="body-03 · Atkinson Regular · 14px" wide>
              <Span>The quick brown fox jumps over the lazy dog</Span>
            </Item>
          </Row>
          <Row title="Others" col>
            <Item
              label="Terms & Captions · Atkinson Regular · 10px / 0.32px tracking"
              wide
            >
              <Caption>
                This is for captions or legal content in a layout
              </Caption>
            </Item>
          </Row>
        </Section>

        {/* ── Button ── */}
        <Section title="Atoms · Button">
          {/* Primary */}
          <Row title="Primary · sm">
            <Item label="default">
              <Button variant="primary" size="sm">
                Button
              </Button>
            </Item>
            <Item label="leadingIcon">
              <Button variant="primary" size="sm" leadingIcon={SaveIcon}>
                Save
              </Button>
            </Item>
            <Item label="trailingIcon">
              <Button
                variant="primary"
                size="sm"
                trailingIcon={ChevronRightIcon}
              >
                Next
              </Button>
            </Item>
            <Item label="loading">
              <Button variant="primary" size="sm" loading>
                Button
              </Button>
            </Item>
            <Item label="disabled">
              <Button variant="primary" size="sm" disabled>
                Button
              </Button>
            </Item>
            <Item label="fullWidth" wide>
              <Button
                variant="primary"
                size="sm"
                fullWidth
                leadingIcon={SaveIcon}
              >
                Save
              </Button>
            </Item>
          </Row>
          <Row title="Primary · md">
            <Item label="default">
              <Button variant="primary" size="md">
                Button
              </Button>
            </Item>
            <Item label="leadingIcon">
              <Button variant="primary" size="md" leadingIcon={SaveIcon}>
                Save
              </Button>
            </Item>
            <Item label="trailingIcon">
              <Button
                variant="primary"
                size="md"
                trailingIcon={ChevronRightIcon}
              >
                Next
              </Button>
            </Item>
            <Item label="loading">
              <Button variant="primary" size="md" loading>
                Button
              </Button>
            </Item>
            <Item label="disabled">
              <Button variant="primary" size="md" disabled>
                Button
              </Button>
            </Item>
            <Item label="fullWidth" wide>
              <Button
                variant="primary"
                size="md"
                fullWidth
                leadingIcon={SaveIcon}
              >
                Save
              </Button>
            </Item>
          </Row>
          <Row title="Primary · lg">
            <Item label="default">
              <Button variant="primary" size="lg">
                Button
              </Button>
            </Item>
            <Item label="leadingIcon">
              <Button variant="primary" size="lg" leadingIcon={SaveIcon}>
                Save
              </Button>
            </Item>
            <Item label="trailingIcon">
              <Button
                variant="primary"
                size="lg"
                trailingIcon={ChevronRightIcon}
              >
                Next
              </Button>
            </Item>
            <Item label="loading">
              <Button variant="primary" size="lg" loading>
                Button
              </Button>
            </Item>
            <Item label="disabled">
              <Button variant="primary" size="lg" disabled>
                Button
              </Button>
            </Item>
            <Item label="fullWidth" wide>
              <Button
                variant="primary"
                size="lg"
                fullWidth
                leadingIcon={SaveIcon}
              >
                Save
              </Button>
            </Item>
          </Row>
          {/* Secondary */}
          <Row title="Secondary · sm">
            <Item label="default">
              <Button variant="secondary" size="sm">
                Button
              </Button>
            </Item>
            <Item label="leadingIcon">
              <Button variant="secondary" size="sm" leadingIcon={SaveIcon}>
                Save
              </Button>
            </Item>
            <Item label="trailingIcon">
              <Button
                variant="secondary"
                size="sm"
                trailingIcon={ChevronRightIcon}
              >
                Next
              </Button>
            </Item>
            <Item label="loading">
              <Button variant="secondary" size="sm" loading>
                Button
              </Button>
            </Item>
            <Item label="disabled">
              <Button variant="secondary" size="sm" disabled>
                Button
              </Button>
            </Item>
            <Item label="fullWidth" wide>
              <Button
                variant="secondary"
                size="sm"
                fullWidth
                leadingIcon={SaveIcon}
              >
                Save
              </Button>
            </Item>
          </Row>
          <Row title="Secondary · md">
            <Item label="default">
              <Button variant="secondary" size="md">
                Button
              </Button>
            </Item>
            <Item label="leadingIcon">
              <Button variant="secondary" size="md" leadingIcon={SaveIcon}>
                Save
              </Button>
            </Item>
            <Item label="trailingIcon">
              <Button
                variant="secondary"
                size="md"
                trailingIcon={ChevronRightIcon}
              >
                Next
              </Button>
            </Item>
            <Item label="loading">
              <Button variant="secondary" size="md" loading>
                Button
              </Button>
            </Item>
            <Item label="disabled">
              <Button variant="secondary" size="md" disabled>
                Button
              </Button>
            </Item>
            <Item label="fullWidth" wide>
              <Button
                variant="secondary"
                size="md"
                fullWidth
                leadingIcon={SaveIcon}
              >
                Save
              </Button>
            </Item>
          </Row>
          <Row title="Secondary · lg">
            <Item label="default">
              <Button variant="secondary" size="lg">
                Button
              </Button>
            </Item>
            <Item label="leadingIcon">
              <Button variant="secondary" size="lg" leadingIcon={SaveIcon}>
                Save
              </Button>
            </Item>
            <Item label="trailingIcon">
              <Button
                variant="secondary"
                size="lg"
                trailingIcon={ChevronRightIcon}
              >
                Next
              </Button>
            </Item>
            <Item label="loading">
              <Button variant="secondary" size="lg" loading>
                Button
              </Button>
            </Item>
            <Item label="disabled">
              <Button variant="secondary" size="lg" disabled>
                Button
              </Button>
            </Item>
            <Item label="fullWidth" wide>
              <Button
                variant="secondary"
                size="lg"
                fullWidth
                leadingIcon={SaveIcon}
              >
                Save
              </Button>
            </Item>
          </Row>
          {/* Tertiary */}
          <Row title="Tertiary · sm">
            <Item label="default">
              <Button variant="tertiary" size="sm">
                Button
              </Button>
            </Item>
            <Item label="leadingIcon">
              <Button variant="tertiary" size="sm" leadingIcon={SaveIcon}>
                Save
              </Button>
            </Item>
            <Item label="trailingIcon">
              <Button
                variant="tertiary"
                size="sm"
                trailingIcon={ChevronRightIcon}
              >
                Next
              </Button>
            </Item>
            <Item label="loading">
              <Button variant="tertiary" size="sm" loading>
                Button
              </Button>
            </Item>
            <Item label="disabled">
              <Button variant="tertiary" size="sm" disabled>
                Button
              </Button>
            </Item>
            <Item label="fullWidth" wide>
              <Button
                variant="tertiary"
                size="sm"
                fullWidth
                leadingIcon={SaveIcon}
              >
                Save
              </Button>
            </Item>
          </Row>
          <Row title="Tertiary · md">
            <Item label="default">
              <Button variant="tertiary" size="md">
                Button
              </Button>
            </Item>
            <Item label="leadingIcon">
              <Button variant="tertiary" size="md" leadingIcon={SaveIcon}>
                Save
              </Button>
            </Item>
            <Item label="trailingIcon">
              <Button
                variant="tertiary"
                size="md"
                trailingIcon={ChevronRightIcon}
              >
                Next
              </Button>
            </Item>
            <Item label="loading">
              <Button variant="tertiary" size="md" loading>
                Button
              </Button>
            </Item>
            <Item label="disabled">
              <Button variant="tertiary" size="md" disabled>
                Button
              </Button>
            </Item>
            <Item label="fullWidth" wide>
              <Button
                variant="tertiary"
                size="md"
                fullWidth
                leadingIcon={SaveIcon}
              >
                Save
              </Button>
            </Item>
          </Row>
          <Row title="Tertiary · lg">
            <Item label="default">
              <Button variant="tertiary" size="lg">
                Button
              </Button>
            </Item>
            <Item label="leadingIcon">
              <Button variant="tertiary" size="lg" leadingIcon={SaveIcon}>
                Save
              </Button>
            </Item>
            <Item label="trailingIcon">
              <Button
                variant="tertiary"
                size="lg"
                trailingIcon={ChevronRightIcon}
              >
                Next
              </Button>
            </Item>
            <Item label="loading">
              <Button variant="tertiary" size="lg" loading>
                Button
              </Button>
            </Item>
            <Item label="disabled">
              <Button variant="tertiary" size="lg" disabled>
                Button
              </Button>
            </Item>
            <Item label="fullWidth" wide>
              <Button
                variant="tertiary"
                size="lg"
                fullWidth
                leadingIcon={SaveIcon}
              >
                Save
              </Button>
            </Item>
          </Row>
          {/* Link */}
          <Row title="Link · sm">
            <Item label="default">
              <Button variant="link" size="sm">
                Link
              </Button>
            </Item>
            <Item label="leadingIcon">
              <Button variant="link" size="sm" leadingIcon={SaveIcon}>
                Link
              </Button>
            </Item>
            <Item label="trailingIcon">
              <Button variant="link" size="sm" trailingIcon={ChevronRightIcon}>
                Link
              </Button>
            </Item>
            <Item label="disabled">
              <Button variant="link" size="sm" disabled>
                Link
              </Button>
            </Item>
            <Item label="loading">
              <Button variant="link" size="sm" loading>
                Link
              </Button>
            </Item>
          </Row>
          <Row title="Link · md">
            <Item label="default">
              <Button variant="link" size="md">
                Link
              </Button>
            </Item>
            <Item label="leadingIcon">
              <Button variant="link" size="md" leadingIcon={SaveIcon}>
                Link
              </Button>
            </Item>
            <Item label="trailingIcon">
              <Button variant="link" size="md" trailingIcon={ChevronRightIcon}>
                Link
              </Button>
            </Item>
            <Item label="disabled">
              <Button variant="link" size="md" disabled>
                Link
              </Button>
            </Item>
            <Item label="loading">
              <Button variant="link" size="md" loading>
                Link
              </Button>
            </Item>
          </Row>
          <Row title="Link · lg">
            <Item label="default">
              <Button variant="link" size="lg">
                Link
              </Button>
            </Item>
            <Item label="leadingIcon">
              <Button variant="link" size="lg" leadingIcon={SaveIcon}>
                Link
              </Button>
            </Item>
            <Item label="trailingIcon">
              <Button variant="link" size="lg" trailingIcon={ChevronRightIcon}>
                Link
              </Button>
            </Item>
            <Item label="disabled">
              <Button variant="link" size="lg" disabled>
                Link
              </Button>
            </Item>
            <Item label="loading">
              <Button variant="link" size="lg" loading>
                Link
              </Button>
            </Item>
          </Row>
        </Section>

        {/* ── ActionButton ── */}
        <Section title="Atoms · ActionButton">
          {/* Primary */}
          <Row title="Primary · lg">
            <Item label="icon">
              <ActionButton
                variant="primary"
                size="lg"
                icon={ChevronDownIcon}
              />
            </Item>
            <Item label="dot">
              <ActionButton variant="primary" size="lg" dot icon={BellIcon} />
            </Item>
            <Item label="number">
              <ActionButton variant="primary" size="lg" number={1} />
            </Item>
            <Item label="disabled">
              <ActionButton
                variant="primary"
                size="lg"
                disabled
                icon={ChevronDownIcon}
              />
            </Item>
            <Item label="disabled dot">
              <ActionButton
                variant="primary"
                size="lg"
                dot
                disabled
                icon={BellIcon}
              />
            </Item>
          </Row>
          <Row title="Primary · md">
            <Item label="icon">
              <ActionButton
                variant="primary"
                size="md"
                icon={ChevronDownIcon}
              />
            </Item>
            <Item label="dot">
              <ActionButton variant="primary" size="md" dot icon={BellIcon} />
            </Item>
            <Item label="number">
              <ActionButton variant="primary" size="md" number={1} />
            </Item>
            <Item label="disabled">
              <ActionButton
                variant="primary"
                size="md"
                disabled
                icon={ChevronDownIcon}
              />
            </Item>
            <Item label="disabled dot">
              <ActionButton
                variant="primary"
                size="md"
                dot
                disabled
                icon={BellIcon}
              />
            </Item>
          </Row>
          <Row title="Primary · sm">
            <Item label="icon">
              <ActionButton
                variant="primary"
                size="sm"
                icon={ChevronDownIcon}
              />
            </Item>
            <Item label="dot">
              <ActionButton variant="primary" size="sm" dot icon={BellIcon} />
            </Item>
            <Item label="number">
              <ActionButton variant="primary" size="sm" number={1} />
            </Item>
            <Item label="disabled">
              <ActionButton
                variant="primary"
                size="sm"
                disabled
                icon={ChevronDownIcon}
              />
            </Item>
            <Item label="disabled dot">
              <ActionButton
                variant="primary"
                size="sm"
                dot
                disabled
                icon={BellIcon}
              />
            </Item>
          </Row>
          {/* Secondary */}
          <Row title="Secondary · lg">
            <Item label="icon">
              <ActionButton
                variant="secondary"
                size="lg"
                icon={ChevronDownIcon}
              />
            </Item>
            <Item label="dot">
              <ActionButton variant="secondary" size="lg" dot icon={BellIcon} />
            </Item>
            <Item label="number">
              <ActionButton variant="secondary" size="lg" number={1} />
            </Item>
            <Item label="disabled">
              <ActionButton
                variant="secondary"
                size="lg"
                disabled
                icon={ChevronDownIcon}
              />
            </Item>
            <Item label="disabled dot">
              <ActionButton
                variant="secondary"
                size="lg"
                dot
                disabled
                icon={BellIcon}
              />
            </Item>
          </Row>
          <Row title="Secondary · md">
            <Item label="icon">
              <ActionButton
                variant="secondary"
                size="md"
                icon={ChevronDownIcon}
              />
            </Item>
            <Item label="dot">
              <ActionButton variant="secondary" size="md" dot icon={BellIcon} />
            </Item>
            <Item label="number">
              <ActionButton variant="secondary" size="md" number={1} />
            </Item>
            <Item label="disabled">
              <ActionButton
                variant="secondary"
                size="md"
                disabled
                icon={ChevronDownIcon}
              />
            </Item>
            <Item label="disabled dot">
              <ActionButton
                variant="secondary"
                size="md"
                dot
                disabled
                icon={BellIcon}
              />
            </Item>
          </Row>
          <Row title="Secondary · sm">
            <Item label="icon">
              <ActionButton
                variant="secondary"
                size="sm"
                icon={ChevronDownIcon}
              />
            </Item>
            <Item label="dot">
              <ActionButton variant="secondary" size="sm" dot icon={BellIcon} />
            </Item>
            <Item label="number">
              <ActionButton variant="secondary" size="sm" number={1} />
            </Item>
            <Item label="disabled">
              <ActionButton
                variant="secondary"
                size="sm"
                disabled
                icon={ChevronDownIcon}
              />
            </Item>
            <Item label="disabled dot">
              <ActionButton
                variant="secondary"
                size="sm"
                dot
                disabled
                icon={BellIcon}
              />
            </Item>
          </Row>
          {/* Tertiary */}
          <Row title="Tertiary · lg">
            <Item label="icon">
              <ActionButton
                variant="tertiary"
                size="lg"
                icon={ChevronDownIcon}
              />
            </Item>
            <Item label="dot">
              <ActionButton variant="tertiary" size="lg" dot icon={BellIcon} />
            </Item>
            <Item label="number">
              <ActionButton variant="tertiary" size="lg" number={1} />
            </Item>
            <Item label="disabled">
              <ActionButton
                variant="tertiary"
                size="lg"
                disabled
                icon={ChevronDownIcon}
              />
            </Item>
            <Item label="disabled dot">
              <ActionButton
                variant="tertiary"
                size="lg"
                dot
                disabled
                icon={BellIcon}
              />
            </Item>
          </Row>
          <Row title="Tertiary · md">
            <Item label="icon">
              <ActionButton
                variant="tertiary"
                size="md"
                icon={ChevronDownIcon}
              />
            </Item>
            <Item label="dot">
              <ActionButton variant="tertiary" size="md" dot icon={BellIcon} />
            </Item>
            <Item label="number">
              <ActionButton variant="tertiary" size="md" number={1} />
            </Item>
            <Item label="disabled">
              <ActionButton
                variant="tertiary"
                size="md"
                disabled
                icon={ChevronDownIcon}
              />
            </Item>
            <Item label="disabled dot">
              <ActionButton
                variant="tertiary"
                size="md"
                dot
                disabled
                icon={BellIcon}
              />
            </Item>
          </Row>
          <Row title="Tertiary · sm">
            <Item label="icon">
              <ActionButton
                variant="tertiary"
                size="sm"
                icon={ChevronDownIcon}
              />
            </Item>
            <Item label="dot">
              <ActionButton variant="tertiary" size="sm" dot icon={BellIcon} />
            </Item>
            <Item label="number">
              <ActionButton variant="tertiary" size="sm" number={1} />
            </Item>
            <Item label="disabled">
              <ActionButton
                variant="tertiary"
                size="sm"
                disabled
                icon={ChevronDownIcon}
              />
            </Item>
            <Item label="disabled dot">
              <ActionButton
                variant="tertiary"
                size="sm"
                dot
                disabled
                icon={BellIcon}
              />
            </Item>
          </Row>
        </Section>

        {/* ── Link ── */}
        <Section title="Atoms · Link">
          <Row title="Default">
            <Item label="Link">
              <Link href="#">Anchor link</Link>
            </Item>
          </Row>
        </Section>

        {/* ── Input ── */}
        <Section title="Atoms · Input">
          <Row title="Base" col>
            <Item label="placeholder only" wide>
              <Input placeholder="Placeholder" />
            </Item>
            <Item label="with label" wide>
              <Input id="ds-label" label="Label" placeholder="Placeholder" />
            </Item>
            <Item label="label + help text" wide>
              <Input
                id="ds-help"
                label="Label"
                placeholder="Placeholder"
                helpText="This is a help text"
              />
            </Item>
          </Row>
          <Row title="Props" col>
            <Item label="tooltip" wide>
              <Input
                id="ds-tooltip"
                label="Label"
                placeholder="Placeholder"
                tooltip="This is a tooltip with extra information"
              />
            </Item>
            <Item label="optional" wide>
              <Input
                id="ds-optional"
                label="Label"
                placeholder="Placeholder"
                optional
                helpText="This field is not required"
              />
            </Item>
            <Item label="trailing icon" wide>
              <Input
                id="ds-icon"
                label="Label"
                placeholder="Placeholder"
                icon={<SaveIcon className="h-5 w-5 text-neutral-300" />}
              />
            </Item>
          </Row>
          <Row title="States" col>
            <Item label="error (visual only)" wide>
              <Input
                id="ds-error-bool"
                label="Label"
                placeholder="Placeholder"
                defaultValue="Bad input"
                error
              />
            </Item>
            <Item label="error with message" wide>
              <Input
                id="ds-error-msg"
                label="Label"
                placeholder="Placeholder"
                defaultValue="Bad input"
                error="This value is not valid"
              />
            </Item>
            <Item label="disabled" wide>
              <Input
                id="ds-disabled"
                label="Label"
                placeholder="Placeholder"
                helpText="This field is disabled"
                disabled
              />
            </Item>
          </Row>
        </Section>

        {/* ── Textarea ── */}
        <Section title="Atoms · Textarea">
          <Row title="States">
            <Item label="default" wide>
              <Textarea
                id="ds-textarea"
                label="Description"
                placeholder="Tell us what happened"
                value={textareaValue}
                onChange={(e) => setTextareaValue(e.target.value)}
              />
            </Item>
            <Item label="optional + help" wide>
              <Textarea
                label="Notes"
                optional
                helpText="Max 500 characters"
                placeholder="Placeholder"
              />
            </Item>
            <Item label="error" wide>
              <Textarea
                label="Description"
                error="This field is required"
                placeholder="Placeholder"
              />
            </Item>
            <Item label="disabled" wide>
              <Textarea label="Description" disabled placeholder="Disabled" />
            </Item>
          </Row>
        </Section>

        {/* ── SelectField ── */}
        <Section title="Molecules · SelectField">
          <Row title="Base" col>
            <Item label="default" wide>
              <SelectField
                id="ds-select"
                label="Pet type"
                value={selectValue}
                options={PET_OPTIONS}
                placeholder="Select a pet"
                onSelect={setSelectValue}
              />
            </Item>
            <Item label="with help text" wide>
              <SelectField
                id="ds-select-help"
                label="Pet type"
                value={selectValue}
                options={PET_OPTIONS}
                placeholder="Select a pet"
                helpText="Choose the type of pet you'd like to insure"
                onSelect={setSelectValue}
              />
            </Item>
            <Item label="searchable" wide>
              <SelectField
                id="ds-select-search"
                label="Pet type (searchable)"
                value={searchSelectValue}
                options={PET_OPTIONS}
                placeholder="Select a pet"
                searchPlaceholder="Search pets…"
                searchable
                onSelect={setSearchSelectValue}
              />
            </Item>
          </Row>
          <Row title="Props" col>
            <Item label="tooltip" wide>
              <SelectField
                id="ds-select-tooltip"
                label="Pet type"
                value={selectValue}
                options={PET_OPTIONS}
                placeholder="Select a pet"
                tooltip="We cover dogs, cats, and more"
                onSelect={setSelectValue}
              />
            </Item>
            <Item label="optional" wide>
              <SelectField
                id="ds-select-optional"
                label="Pet type"
                value={selectValue}
                options={PET_OPTIONS}
                placeholder="Select a pet"
                optional
                onSelect={setSelectValue}
              />
            </Item>
            <Item label="optionsPlaceholder" wide>
              <SelectField
                id="ds-select-options-placeholder"
                label="Vet (searchable)"
                value={searchSelectValue}
                options={[]}
                placeholder="Select a vet"
                searchPlaceholder="Search vets…"
                optionsPlaceholder="Search your vet"
                searchable
                onSelect={setSearchSelectValue}
              />
            </Item>
          </Row>
          <Row title="States" col>
            <Item label="error with message" wide>
              <SelectField
                id="ds-select-error"
                label="Pet type"
                value=""
                options={PET_OPTIONS}
                placeholder="Select a pet"
                error="Please select a pet"
                onSelect={() => {}}
              />
            </Item>
            <Item label="error (visual only)" wide>
              <SelectField
                id="ds-select-error-bool"
                label="Pet type"
                value=""
                options={PET_OPTIONS}
                placeholder="Select a pet"
                error
                onSelect={() => {}}
              />
            </Item>
            <Item label="disabled" wide>
              <SelectField
                id="ds-select-disabled"
                label="Pet type"
                value=""
                options={PET_OPTIONS}
                placeholder="Select a pet"
                helpText="Selection is not available"
                disabled
                onSelect={() => {}}
              />
            </Item>
          </Row>
        </Section>

        {/* ── DatePickerField ── */}
        <Section title="Molecules · DatePickerField">
          <Row title="Base">
            <Item label="Default" wide>
              <DatePickerField
                label="Date"
                value={dateValue}
                onChange={setDateValue}
              />
            </Item>
            <Item label="With help text" wide>
              <DatePickerField
                label="Date"
                value={dateValue}
                onChange={setDateValue}
                helpText="Select the date of the incident"
              />
            </Item>
            <Item label="Optional" wide>
              <DatePickerField
                label="Date"
                value={dateValue}
                onChange={setDateValue}
                optional
              />
            </Item>
          </Row>
          <Row title="Actions variant">
            <Item label="With confirm / cancel" wide>
              <DatePickerField
                label="Date"
                value={dateValue}
                withActions
                onChange={setDateValue}
              />
            </Item>
          </Row>
          <Row title="Limited dates">
            <Item label="Min date (Current date)" wide>
              <DatePickerField
                label="Date"
                value={limitedDateValue}
                minDate={dateMin}
                onChange={setLimitedDateValue}
              />
            </Item>
            <Item label="Max date (One month from now)" wide>
              <DatePickerField
                label="Date"
                value={limitedDateValue}
                maxDate={dateMax}
                onChange={setLimitedDateValue}
              />
            </Item>
            <Item label="Min - current date · Max - one month from now" wide>
              <DatePickerField
                label="Date"
                value={limitedDateValue}
                minDate={dateMin}
                maxDate={dateMax}
                onChange={setLimitedDateValue}
              />
            </Item>
          </Row>
          <Row title="States">
            <Item label="Error with message" wide>
              <DatePickerField
                label="Date"
                value={undefined}
                error="Please select a valid date"
                onChange={() => {}}
              />
            </Item>
            <Item label="Disabled" wide>
              <DatePickerField
                label="Date"
                value={new Date(2026, 4, 15)}
                disabled
                onChange={() => {}}
              />
            </Item>
          </Row>
        </Section>

        {/* ── DateRangePickerField ── */}
        <Section title="Molecules · DateRangePickerField">
          <Row title="Base">
            <Item label="Default" wide>
              <DateRangePickerField
                label="Date range"
                value={dateRange}
                onChange={setDateRange}
              />
            </Item>
            <Item label="With help text" wide>
              <DateRangePickerField
                label="Date range"
                value={dateRange}
                onChange={setDateRange}
                helpText="Select start and end dates"
              />
            </Item>
          </Row>
          <Row title="Actions variant">
            <Item label="With confirm / cancel" wide>
              <DateRangePickerField
                label="Date range"
                value={dateRange}
                withActions
                onChange={setDateRange}
              />
            </Item>
          </Row>
          <Row title="Limited dates">
            <Item label="Min date (Current date)" wide>
              <DateRangePickerField
                label="Date range"
                value={limitedDateRange}
                minDate={dateMin}
                onChange={setLimitedDateRange}
              />
            </Item>
            <Item label="Max date (One month from now)" wide>
              <DateRangePickerField
                label="Date range"
                value={limitedDateRange}
                maxDate={dateMax}
                onChange={setLimitedDateRange}
              />
            </Item>
            <Item label="Min - current date · Max - one month from now" wide>
              <DateRangePickerField
                label="Date range"
                value={limitedDateRange}
                minDate={dateMin}
                maxDate={dateMax}
                onChange={setLimitedDateRange}
              />
            </Item>
          </Row>
          <Row title="States">
            <Item label="Error" wide>
              <DateRangePickerField
                label="Date range"
                value={{ start: undefined, end: undefined }}
                error="Please select a valid date range"
                onChange={() => {}}
              />
            </Item>
            <Item label="Disabled" wide>
              <DateRangePickerField
                label="Date range"
                value={{
                  start: new Date(2026, 4, 5),
                  end: new Date(2026, 4, 16),
                }}
                disabled
                onChange={() => {}}
              />
            </Item>
          </Row>
        </Section>

        {/* ── LoadingDots ── */}
        <Section title="Atoms · Loading Dots">
          <Row title="Sizes">
            <Item label="sm">
              <LoadingDots className="loading-sm" />
            </Item>
            <Item label="md (default)">
              <LoadingDots />
            </Item>
            <Item label="lg">
              <LoadingDots className="loading-lg" />
            </Item>
          </Row>
        </Section>

        {/* ── Toast ── */}
        <Section title="Molecules · Toast">
          <Row title="Trigger variants">
            <Item label="success">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setToastVariant('success')}
              >
                Success
              </Button>
            </Item>
            <Item label="error">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setToastVariant('error')}
              >
                Error
              </Button>
            </Item>
            <Item label="info">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setToastVariant('info')}
              >
                Info
              </Button>
            </Item>
          </Row>
          {toastVariant && (
            <Toast
              message={`This is a ${toastVariant} toast message.`}
              variant={toastVariant}
              onDismiss={() => setToastVariant(undefined)}
            />
          )}
        </Section>

        {/* ── RadioInput ── */}
        <Section title="Atoms · RadioInput">
          <Row title="Options">
            {PET_OPTIONS.map((opt) => (
              <Item key={opt.value} label={opt.value}>
                <RadioInput
                  name="ds-radio"
                  label={opt.label}
                  value={opt.value}
                  checked={radioValue === opt.value}
                  onChange={() => setRadioValue(opt.value)}
                />
              </Item>
            ))}
          </Row>
          <Row title="Disabled">
            <Item label="unchecked">
              <RadioInput
                name="ds-radio-disabled"
                label="Unchecked"
                value="a"
                disabled
              />
            </Item>
            <Item label="checked">
              <RadioInput
                name="ds-radio-disabled"
                label="Checked"
                value="b"
                checked
                disabled
                readOnly
              />
            </Item>
          </Row>
        </Section>

        {/* ── CheckboxInput ── */}
        <Section title="Atoms · CheckboxInput">
          <Row title="Default">
            <Item label="unchecked">
              <CheckboxInput
                label="Dog"
                checked={checkboxDog}
                onChange={(e) => setCheckboxDog(e.target.checked)}
              />
            </Item>
            <Item label="checked">
              <CheckboxInput
                label="Cat"
                checked={checkboxCat}
                onChange={(e) => setCheckboxCat(e.target.checked)}
              />
            </Item>
          </Row>
          <Row title="Disabled">
            <Item label="unchecked">
              <CheckboxInput label="Dog" disabled />
            </Item>
            <Item label="checked">
              <CheckboxInput label="Cat" checked disabled readOnly />
            </Item>
          </Row>
        </Section>

        {/* ── LinkCard ── */}
        <Section title="Molecules · LinkCard">
          <Row title="Variants" col>
            <Item label="with router link" wide>
              <LinkCard label="Go to payment" to="/payment" />
            </Item>
            <Item label="with onClick" wide>
              <LinkCard label="Trigger action" onClick={() => {}} />
            </Item>
          </Row>
        </Section>

        {/* ── OtpInput ── */}
        <Section title="Molecules · OtpInput">
          <Row title="Default">
            <OtpInput onChange={() => {}} preventAutoFocus />
          </Row>
          <Row title="Loading">
            <OtpInput onChange={() => {}} loading />
          </Row>
        </Section>

        {/* ── ResendOtp ── */}
        <Section title="Molecules · ResendOtp">
          <Row title="Default">
            <ResendOtp onResend={() => {}} />
          </Row>
        </Section>

        {/* ── PolicyCard ── */}
        <Section title="Molecules · PolicyCard">
          <Row title="Variants">
            <PolicyCard
              policy={{
                displayName: 'Buddy',
                displayAmount: '$25.00',
                isInArrears: false,
                pet: { type: 'dog' },
              }}
            />
            <PolicyCard
              policy={{
                displayName: 'Whiskers',
                displayAmount: '$18.50',
                isInArrears: false,
                pet: { type: 'cat' },
              }}
            />
            <PolicyCard
              policy={{
                displayName: 'Buddy',
                displayAmount: '$25.00',
                isInArrears: true,
                arrearsDisplay: { days: 5, amount: '$25.00' },
                pet: { type: 'dog' },
              }}
            />
          </Row>
        </Section>

        {/* ── IconTile ── */}
        <Section title="Molecules · IconTile">
          <Row title="Default (no interaction)">
            <Item label="short label">
              <IconTile
                icon={<BellIcon className="text-indigo-base h-6 w-6" />}
                label="Notifications"
              />
            </Item>
            <Item label="long label">
              <IconTile
                icon={<ChevronRightIcon className="text-indigo-base h-6 w-6" />}
                label="Damage to someone else's property"
              />
            </Item>
          </Row>
        </Section>

        {/* ── QuickActionButton ── */}
        <Section title="Atoms · QuickActionButton">
          <Row title="Row (link / button)" col>
            <Item label="4 actions">
              <div className="flex w-full min-w-full items-stretch gap-2">
                <QuickActionButton
                  icon={easyClaims}
                  label="Reimburse & track a claim"
                  onClick={() => {}}
                  className="flex-1"
                />
                <QuickActionButton
                  icon={manageYourPolicy}
                  label="Download Documents"
                  onClick={() => {}}
                  className="flex-1"
                />
                <QuickActionButton
                  icon={getPaidBack}
                  label="Payment"
                  onClick={() => {}}
                  className="flex-1"
                />
                <QuickActionButton
                  icon={agentsMan}
                  label="Contact us"
                  onClick={() => {}}
                  className="flex-1"
                />
              </div>
            </Item>
          </Row>
          <Row title="Fixed size (mobile scroller)">
            <Item label="113 × 102">
              <QuickActionButton
                icon={easyClaims}
                label="Reimburse & track a claim"
                onClick={() => {}}
                className="aspect-auto! h-25.5! w-28.25!"
              />
            </Item>
          </Row>
        </Section>

        {/* ── GoalCard ── */}
        <Section title="Molecules · GoalCard">
          <Row title="Layouts / sizes / optional link">
            <Item label="icon-right · sm">
              <GoalCard
                className="w-43.5"
                layout="icon-right"
                icon={exclusiveBenefits}
                description={
                  <>
                    Your waiting period ends in <strong>10 Days</strong>
                  </>
                }
              />
            </Item>
            <Item label="icon-top · md · link">
              <GoalCard
                className="w-43.5"
                size="md"
                icon={invoice}
                description={
                  <>
                    <strong>What’s covered</strong> in your plan?
                  </>
                }
                link={{ label: 'See Details', onClick: () => undefined }}
              />
            </Item>
            <Item label="icon-top · lg · link">
              <GoalCard
                className="w-43.5"
                size="lg"
                icon={money}
                description={
                  <>
                    <strong>Confirm</strong> your{' '}
                    <strong>payment method</strong>
                  </>
                }
                link={{ label: 'Confirm now', onClick: () => undefined }}
              />
            </Item>
            <Item label="icon-top · sm · no link">
              <GoalCard
                className="w-43.5"
                icon={downloadDocuments}
                description={
                  <>
                    <strong>Download</strong> documents
                  </>
                }
              />
            </Item>
          </Row>
        </Section>

        {/* ── PerkCard ── */}
        <Section title="Molecules · PerkCard">
          <Row title="Accents">
            <Item label="coral">
              <PerkCard
                className="w-50"
                image={perkTelevet}
                title="TeleVet"
                description={
                  <>
                    24/7 Virtual Vet.{' '}
                    <strong>
                      Unlimited video and chat with certified vets.
                    </strong>{' '}
                    Save time, money, and stress from home.
                  </>
                }
                action={{ label: 'Call a Vet', onClick: () => undefined }}
              />
            </Item>
            <Item label="indigo">
              <PerkCard
                className="w-50"
                accent="indigo"
                image={perkDirectVet}
                title="Direct vet payment"
                description={
                  <>
                    <strong>
                      For eligible claims over $900, we can pay your vet
                      directly
                    </strong>{' '}
                    so you can focus on your pet, not the bill.
                  </>
                }
                action={{ label: 'Know more', onClick: () => undefined }}
              />
            </Item>
          </Row>
        </Section>

        {/* ── BillingCard ── */}
        <Section title="Molecules · BillingCard">
          <Row title="Image fallback / with image">
            <Item label="no image">
              <BillingCard
                className="w-51"
                name="Oliver"
                period="July 2026"
                status="Paid"
                amount="$45.00"
                downloadLabel="Download"
                onDownload={() => undefined}
              />
            </Item>
            <Item label="with image">
              <BillingCard
                className="w-51"
                imageUrl={goodDog}
                name="Oliver"
                period="July 2026"
                status="Paid"
                amount="$45.00"
                downloadLabel="Download"
                onDownload={() => undefined}
              />
            </Item>
          </Row>
        </Section>

        {/* ── ProfileMenu ── */}
        <Section title="Molecules · ProfileMenu">
          <Row title="Click to open">
            <Item label="default">
              <ProfileMenu name="Paula Durán" onLogout={() => undefined} />
            </Item>
          </Row>
        </Section>

        {/* ── PromoCard ── */}
        <Section title="Molecules · PromoCard">
          <Row title="Variants (stacked below md)" col>
            <Item label="coral">
              <PromoCard
                variant="coral"
                className="w-[calc(100vw-4rem)] max-w-90.25 md:w-106 md:max-w-none"
                title="Referer a"
                titleHighlight="Friend"
                description={
                  <>
                    Earn <strong>Amazon $25</strong> by inviting{' '}
                    <br className="md:hidden" />
                    friends and family to kanguro insurance.
                  </>
                }
                action={{ label: 'Let’s do it', onClick: () => {} }}
              />
            </Item>
            <Item label="indigo">
              <PromoCard
                variant="indigo"
                className="w-[calc(100vw-4rem)] max-w-90.25 md:w-106 md:max-w-none"
                title="Do you"
                titleHighlight="Also Rent?"
                description={
                  <>
                    Life happens at home too.
                    <br />
                    <strong>Protect your belongings</strong>
                    <br />
                    as low as $5/month.
                  </>
                }
                action={{ label: 'See my price', to: '/renters' }}
              />
            </Item>
          </Row>
          <Row title="Compact layout (desktop only)" col>
            <Item label="coral · compact">
              <PromoCard
                variant="coral"
                layout="compact"
                className="w-[calc(100vw-4rem)] max-w-90.25 md:w-106 md:max-w-none"
                title="Referer a"
                titleHighlight="Friend"
                description={
                  <>
                    Earn <strong>Amazon $25</strong> by inviting friends and
                    family to kanguro insurance.
                  </>
                }
                action={{ label: 'Let’s do it', onClick: () => {} }}
              />
            </Item>
            <Item label="indigo · compact">
              <PromoCard
                variant="indigo"
                layout="compact"
                className="w-[calc(100vw-4rem)] max-w-90.25 md:w-106 md:max-w-none"
                title="Do you"
                titleHighlight="Also Rent?"
                description={
                  <>
                    Life happens at home too.{' '}
                    <strong>Protect your belongings</strong> as low as $5/month.
                  </>
                }
                action={{ label: 'See my price', to: '/renters' }}
              />
            </Item>
          </Row>
        </Section>

        {/* ── AppDownloadBanner ── */}
        <Section title="Molecules · AppDownloadBanner">
          <Row title="Variants" col>
            <Item label="wide (default)">
              <div className="w-[calc(100vw-4rem)] max-w-168">
                <AppDownloadBanner />
              </div>
            </Item>
            <Item label="compact">
              <div className="w-[calc(100vw-4rem)] max-w-106">
                <AppDownloadBanner variant="compact" />
              </div>
            </Item>
          </Row>
        </Section>

        {/* ── FeatureItem ── */}
        <Section title="Molecules · FeatureItem">
          <Row title="Examples" col>
            <Item label="with SVG icon" wide>
              <FeatureItem
                icon={
                  <div className="flex size-full items-center justify-center rounded-xl">
                    <BellIcon className="text-indigo-base h-[4.375rem] w-[4.375rem]" />
                  </div>
                }
                title="Visit any vet."
                description="Go to any licensed veterinarian in the US."
              />
            </Item>
          </Row>
        </Section>

        {/* ── Slider ── */}
        <Section title="Molecules · Slider">
          <Row title="9 items → 2 slides" col>
            <Slider
              items={[
                {
                  label: 'Stars',
                  icon: CoverageFeatureIcons.CoverageStarsIcon,
                },
                {
                  label: 'Policy',
                  icon: CoverageFeatureIcons.CoveragePolicyFolderIcon,
                },
                {
                  label: 'Price',
                  icon: CoverageFeatureIcons.CoveragePriceIcon,
                },
                {
                  label: 'Claim',
                  icon: CoverageFeatureIcons.CoverageClaimIcon,
                },
                {
                  label: 'Growth',
                  icon: CoverageFeatureIcons.CoverageGrowthTrendIcon,
                },
                {
                  label: 'Group Life',
                  icon: CoverageFeatureIcons.CoverageGroupLifeIcon,
                },
                {
                  label: 'Stars',
                  icon: CoverageFeatureIcons.CoverageStarsIcon,
                },
                {
                  label: 'Policy',
                  icon: CoverageFeatureIcons.CoveragePolicyFolderIcon,
                },
                {
                  label: 'Price',
                  icon: CoverageFeatureIcons.CoveragePriceIcon,
                },
              ]}
              renderItem={(item, index) => (
                <IconTile
                  key={index}
                  icon={<item.icon className="text-indigo-base h-6 w-6" />}
                  label={item.label}
                />
              )}
            />
          </Row>
          <Row title="9 items → 2 slides · auto loop (5s)" col>
            <Slider
              autoLoop
              delay={5000}
              items={[
                {
                  label: 'Stars',
                  icon: CoverageFeatureIcons.CoverageStarsIcon,
                },
                {
                  label: 'Policy',
                  icon: CoverageFeatureIcons.CoveragePolicyFolderIcon,
                },
                {
                  label: 'Price',
                  icon: CoverageFeatureIcons.CoveragePriceIcon,
                },
                {
                  label: 'Claim',
                  icon: CoverageFeatureIcons.CoverageClaimIcon,
                },
                {
                  label: 'Growth',
                  icon: CoverageFeatureIcons.CoverageGrowthTrendIcon,
                },
                {
                  label: 'Group Life',
                  icon: CoverageFeatureIcons.CoverageGroupLifeIcon,
                },
                {
                  label: 'Stars',
                  icon: CoverageFeatureIcons.CoverageStarsIcon,
                },
                {
                  label: 'Policy',
                  icon: CoverageFeatureIcons.CoveragePolicyFolderIcon,
                },
                {
                  label: 'Price',
                  icon: CoverageFeatureIcons.CoveragePriceIcon,
                },
              ]}
              renderItem={(item, index) => (
                <IconTile
                  key={index}
                  icon={<item.icon className="text-indigo-base h-6 w-6" />}
                  label={item.label}
                />
              )}
            />
          </Row>
          <Row title="4 items → 1 slide (no dots)" col>
            <Slider
              items={[
                {
                  label: 'Claim',
                  icon: CoverageFeatureIcons.CoverageClaimIcon,
                },
                {
                  label: 'Growth',
                  icon: CoverageFeatureIcons.CoverageGrowthTrendIcon,
                },
                {
                  label: 'Group Life',
                  icon: CoverageFeatureIcons.CoverageGroupLifeIcon,
                },
                {
                  label: 'Stars',
                  icon: CoverageFeatureIcons.CoverageStarsIcon,
                },
              ]}
              renderItem={(item, index) => (
                <IconTile
                  key={index}
                  icon={<item.icon className="text-indigo-base h-6 w-6" />}
                  label={item.label}
                />
              )}
            />
          </Row>
          <Row
            title="list layout · desktop 2 per slide · mobile 1 per slide (no dots)"
            col
          >
            <Slider
              layout="list"
              items={[
                {
                  title: 'Airvet',
                  description: (
                    <>
                      24/7 Virtual Vet.{' '}
                      <span className="text-indigo-base">
                        Unlimited video and chat with certified vets.
                      </span>{' '}
                      Save time, money, and stress from home.
                    </>
                  ),
                },
                {
                  title: 'Wellfold Cashback',
                  description: (
                    <>
                      <span className="text-indigo-base">
                        Cashback on your shopping.
                      </span>{' '}
                      Link your card and earn money back at Sephora, Nike,
                      Target, and more.
                    </>
                  ),
                },
                {
                  title: 'Dental Care',
                  description: (
                    <>
                      <span className="text-indigo-base">
                        Coverage for cleanings and procedures.
                      </span>{' '}
                      Keep your pet's teeth healthy without the surprise bills.
                    </>
                  ),
                },
                {
                  title: 'Emergency Visits',
                  description: (
                    <>
                      24/7 access to emergency care.{' '}
                      <span className="text-indigo-base">
                        We cover up to 90% of eligible costs.
                      </span>
                    </>
                  ),
                },
              ]}
              renderItem={(item, index) => (
                <BenefitCard
                  key={index}
                  image={<div className="size-full bg-indigo-50" />}
                  title={item.title}
                  description={item.description}
                />
              )}
            />
          </Row>
        </Section>

        {/* ── BenefitCard ── */}
        <Section title="Molecules · BenefitCard">
          <Row title="Examples" col>
            <Item label="with highlighted description" wide>
              <BenefitCard
                image={
                  <div className="flex size-full items-center justify-center">
                    <BellIcon className="text-indigo-base h-12 w-12" />
                  </div>
                }
                title="Airvet"
                description={
                  <>
                    {'24/7 Virtual Vet. '}
                    <span className="text-indigo-base">
                      Unlimited video and chat with certified vets.{' '}
                    </span>
                    Save time, money, and stress from home.
                  </>
                }
              />
            </Item>
            <Item label="plain description" wide>
              <BenefitCard
                image={
                  <div className="flex size-full items-center justify-center">
                    <SaveIcon className="text-indigo-base h-12 w-12" />
                  </div>
                }
                title="Easy claims"
                description="Submit a claim in minutes directly from the app, no paperwork needed."
              />
            </Item>
          </Row>
        </Section>

        {/* ── CoverageBanner ── */}
        <Section title="Molecules · CoverageBanner">
          <Row title="Without add-on" col>
            <Item label="default" wide>
              <CoverageBanner
                planName="Essential Coverage"
                policyId="#0987654321"
                startDate="0/0/26"
              />
            </Item>
          </Row>
          <Row title="With add-on" col>
            <Item label="addon" wide>
              <CoverageBanner
                planName="Essential Coverage + Add On"
                policyId="#0987654321"
                startDate="0/0/26"
              />
            </Item>
          </Row>
        </Section>

        {/* ── FileUpload ── */}
        <Section title="Molecules · FileUpload">
          <Row title="Default" col>
            <Item label="video upload" wide>
              <FileUpload label="Add a video of your home" variant="video" />
            </Item>
            <Item label="image upload" wide>
              <FileUpload label="Add a photo of your home" />
            </Item>
            <Item label="document upload · multiple" wide>
              <FileUpload
                label="Invoice or medical records"
                variant="document"
                multiple
                onFilesSelect={addSelectedFiles}
              />
              <SelectedFileList
                files={selectedFiles}
                onRemove={removeSelectedFile}
                className="mt-3"
              />
            </Item>
            <Item label="error" wide>
              <FileUpload
                label="Invoice or medical records"
                variant="document"
                error="Add at least one file"
              />
            </Item>
          </Row>
        </Section>

        {/* ── ClaimCard ── */}
        <Section title="Molecules · ClaimCard">
          <Row title="Tones" col>
            <Item label="info · open" wide>
              <ClaimCard
                petName="Rex"
                claimNumber="#CL-1042"
                statusLabel="In review"
                amount="$150.50"
                date="09/28/26"
              />
            </Item>
            <Item label="success" wide>
              <ClaimCard
                petName="Luna"
                claimNumber="#CL-0988"
                statusLabel="Paid"
                statusTone="success"
                amount="$320.00"
                date="08/14/26"
              />
            </Item>
            <Item label="warning · with message" wide>
              <ClaimCard
                petName="Rex"
                claimNumber="#CL-1051"
                statusLabel="Medical history needed"
                statusTone="warning"
                amount="$89.99"
                date="09/30/26"
                message="Please upload your pet's medical history."
              />
            </Item>
            <Item label="error" wide>
              <ClaimCard
                petName="Luna"
                claimNumber="#CL-0901"
                statusLabel="Denied"
                statusTone="error"
                amount="$45.00"
                date="07/02/26"
              />
            </Item>
          </Row>
        </Section>

        {/* ── SignaturePad ── */}
        <Section title="Molecules · SignaturePad">
          <Row title="Default" col>
            <Item label={hasSignature ? 'signed' : 'empty'} wide>
              <SignaturePad
                label="Your signature"
                onChange={handleSignatureChange}
              />
            </Item>
            <Item label="error" wide>
              <SignaturePad label="Your signature" error="Signature required" />
            </Item>
          </Row>
        </Section>

        {/* ── IdCard ── */}
        <Section title="Molecules · IdCard">
          <Row title="with-photo · no photoUrl" col>
            <Item label="placeholder circle" wide>
              <IdCard
                variant="with-photo"
                planName="Home"
                ownerName="Paula Durán"
                policyId="#12345678"
                startDate="02/02/26"
              />
            </Item>
          </Row>
          <Row title="with-photo · photoUrl" col>
            <Item label="with pet photo" wide>
              <IdCard
                variant="with-photo"
                planName="Accident"
                ownerName="John Smith"
                policyId="#87654321"
                startDate="01/01/26"
                photoUrl="https://placedog.net/92/92"
              />
            </Item>
          </Row>
          <Row title="no-photo" col>
            <Item label="policy info only" wide>
              <IdCard
                variant="no-photo"
                policyId="#12345678"
                birthday="04/12/21"
                weight="8 Kg"
                vetName="Dr. Martínez"
              />
            </Item>
          </Row>

          <Row title="with-photo · mobile variant" col>
            <Item label="placeholder circle" wide>
              <IdCard
                variant="with-photo"
                planName="Home"
                ownerName="Paula Durán"
                policyId="#12345678"
                startDate="02/02/26"
                isMobile
              />
            </Item>
          </Row>
          <Row title="with-photo · link" col>
            <Item label="desktop" wide>
              <IdCard
                variant="with-photo"
                planName="My coverages"
                ownerName="Paula Durán"
                link={{ label: 'See all', onClick: () => {} }}
              />
            </Item>
            <Item label="mobile" wide>
              <IdCard
                variant="with-photo"
                planName="Amapola"
                ownerName="#12345678"
                photoUrl="https://placedog.net/92/92"
                link={{ label: "What's covered", onClick: () => {} }}
                isMobile
              />
            </Item>
          </Row>
          <Row title="no-photo · mobile" col>
            <Item label="policy info only" wide>
              <IdCard
                variant="no-photo"
                policyId="#12345678"
                birthday="04/12/21"
                weight="8 Kg"
                vetName="Dr. Martínez"
                isMobile
              />
            </Item>
          </Row>
        </Section>

        {/* ── Stepper ── */}
        <Section title="Molecules · Stepper">
          <Row title="4-step example">
            <div className="w-full">
              <Stepper
                currentStep={stepperStep}
                className="w-full"
                items={
                  [
                    {
                      label: 'Dog',
                      children: (
                        <div className="flex h-full w-full items-center justify-center gap-4">
                          <img
                            src={goodDog}
                            alt={'Dog'}
                            className="max-h-3/4 max-w-1/4 object-cover"
                          />
                        </div>
                      ),
                    },
                    {
                      label: 'Cat',
                      children: (
                        <div className="flex h-full w-full items-center justify-center gap-4">
                          <img
                            src={goodCat}
                            alt={'Cat'}
                            className="max-h-3/4 max-w-1/4 object-cover"
                          />
                        </div>
                      ),
                    },
                    {
                      label: 'Dog and Cat',
                      children: (
                        <div className="flex h-full w-full items-center justify-center gap-4">
                          <img
                            src={goodDog}
                            alt={'Dog'}
                            className="max-h-3/4 max-w-1/4 object-cover"
                          />
                          <img
                            src={goodCat}
                            alt={'Cat'}
                            className="max-h-3/4 max-w-1/4 object-cover"
                          />
                        </div>
                      ),
                    },
                    {
                      label: 'Done',
                      children: (
                        <div className="flex h-full w-full flex-col items-center justify-center gap-4">
                          <H4>You're all set !</H4>
                          <Button
                            variant="tertiary"
                            size="sm"
                            onClick={() => setStepperStep(0)}
                            leadingIcon={EditIcons.RedoIcon}
                          >
                            Start over
                          </Button>
                        </div>
                      ),
                    },
                  ] satisfies StepperItem[]
                }
              />
              <div className="mt-6 flex w-full items-center justify-center gap-3">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={stepperStep === 0}
                  onClick={() => setStepperStep((s) => s - 1)}
                >
                  Back
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={stepperStep === 3}
                  onClick={() => setStepperStep((s) => s + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </Row>
        </Section>

        {/* ── Modal ── */}
        <Section title="Molecules · Modal">
          <Row title="Default">
            <Item label="open">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setModalOpen(true)}
              >
                Open Modal
              </Button>
            </Item>
          </Row>

          <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}>
            <div className="px-8 py-14">
              <H2 className="mb-4">Modal title</H2>
              <Text className="mb-6 text-neutral-600">
                Centered dialog on desktop, bottom sheet on mobile.
              </Text>
              <Button size="sm" onClick={() => setModalOpen(false)}>
                Close
              </Button>
            </div>
          </Modal>
        </Section>

        {/* ── ActionModal ── */}
        <Section title="Molecules · ActionModal">
          <Row title="Variants">
            {(
              [
                'success',
                'error',
                'warning',
                'info',
                'loading',
                'normal',
              ] as ActionModalVariant[]
            ).map((variant) => (
              <Item key={variant} label={variant}>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => openActionModal(variant)}
                >
                  Open {variant}
                </Button>
              </Item>
            ))}
          </Row>

          <Row title="Actions">
            <Item label="both actions">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => openActionModal('normal', 'both')}
              >
                Both
              </Button>
            </Item>
            <Item label="primary only">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => openActionModal('normal', 'primary')}
              >
                Primary only
              </Button>
            </Item>
            <Item label="secondary only">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => openActionModal('normal', 'secondary')}
              >
                Secondary only
              </Button>
            </Item>
            <Item label="no actions">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => openActionModal('normal', 'none')}
              >
                No actions
              </Button>
            </Item>
          </Row>

          <ActionModal
            isOpen={actionModalOpen}
            variant={actionModalVariant}
            title="Are you sure?"
            description="This action cannot be undone. Please confirm you want to continue."
            primaryAction={
              actionModalActions === 'both' || actionModalActions === 'primary'
                ? { label: 'Confirm', onClick: closeActionModal }
                : undefined
            }
            secondaryAction={
              actionModalActions === 'both' ||
              actionModalActions === 'secondary'
                ? { label: 'Cancel', onClick: closeActionModal }
                : undefined
            }
            onClose={closeActionModal}
          />
        </Section>

        {/* ── Sidebar ── */}
        <Section title="Molecules · Sidebar">
          <Row title="Default">
            <Item label="open">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setSidebarOpen(true)}
              >
                Open Sidebar
              </Button>
            </Item>
          </Row>

          <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)}>
            <div className="px-8 pt-14">
              <H2 className="mb-4">Sidebar title</H2>
              <Text className="mb-6 text-neutral-600">
                Slides up from the bottom on mobile, in from the right on
                desktop.
              </Text>
              <Button size="sm" onClick={() => setSidebarOpen(false)}>
                Close
              </Button>
            </div>
          </Sidebar>
        </Section>

        {/* ── UnderConstruction ── */}
        <Section title="Molecules · UnderConstruction">
          <Row title="Default" col>
            <Item label="fallback for disabled feature flags" wide>
              <UnderConstruction />
            </Item>
          </Row>
        </Section>

        {/* ── Chip ── */}
        <Section title="Molecules · Chip">
          <Row title="States">
            <Item label="default">
              <Chip label="Default" onClick={() => {}} />
            </Item>
            <Item label="selected">
              <Chip
                label="Selected"
                selected={chipSelected}
                onClick={() => setChipSelected((v) => !v)}
              />
            </Item>
            <Item label="disabled">
              <Chip label="Disabled" disabled />
            </Item>
            <Item label="disabled selected">
              <Chip label="Disabled selected" selected disabled />
            </Item>
          </Row>
        </Section>

        {/* ── FilterChips ── */}
        <Section title="Molecules · FilterChips">
          <Row title="Default" col>
            <FilterChips
              filters={[
                { key: 'all', label: 'All' },
                { key: 'dogs', label: 'Dogs' },
                { key: 'cats', label: 'Cats' },
                { key: 'rabbits', label: 'Rabbits', disabled: true },
              ]}
              active={activeFilter}
              onSelect={setActiveFilter}
            />
          </Row>
        </Section>

        {/* ── SelectableCard ── */}
        <Section title="Molecules · SelectableCard">
          <Row title="Checkbox type" col>
            <Item label="unselected" wide>
              <SelectableCard
                label="Dog insurance"
                description="Covers accidents and illness for dogs"
                icon={<BellIcon className="h-5 w-5" />}
                selected={selectableCheckboxA}
                onClick={() => setSelectableCheckboxA((v) => !v)}
              />
            </Item>
            <Item label="selected" wide>
              <SelectableCard
                label="Cat insurance"
                description="Covers accidents and illness for cats"
                icon={<BellIcon className="h-5 w-5" />}
                selected={selectableCheckboxB}
                onClick={() => setSelectableCheckboxB((v) => !v)}
              />
            </Item>
            <Item label="disabled" wide>
              <SelectableCard
                label="Rabbit insurance"
                description="Coming soon"
                disabled
              />
            </Item>
          </Row>
          <Row title="Radio type" col>
            {(['a', 'b', 'c'] as const).map((opt) => (
              <Item key={opt} label={`option-${opt}`} wide>
                <SelectableCard
                  type="radio"
                  label={`Option ${opt.toUpperCase()}`}
                  selected={selectableRadio === opt}
                  onClick={() => setSelectableRadio(opt)}
                />
              </Item>
            ))}
            <Item label="disabled" wide>
              <SelectableCard type="radio" label="Disabled option" disabled />
            </Item>
          </Row>
        </Section>

        {/* ── InfoBanner ── */}
        <Section title="Molecules · InfoBanner">
          <Row title="Default" col>
            <Item label="default" wide>
              <InfoBanner message="Your policy renews automatically on the 1st of each month." />
            </Item>
          </Row>
        </Section>

        {/* ── EmptySection ── */}
        <Section title="Molecules · EmptySection">
          <Row title="Default" col>
            <EmptySection />
          </Row>
        </Section>

        {/* ── LanguageSwitcher ── */}
        <Section title="Molecules · LanguageSwitcher">
          <Row title="Toggle variant">
            <Item label="toggle">
              <LanguageSwitcher variant="toggle" />
            </Item>
          </Row>
          <Row title="Dropdown variant">
            <Item label="dropdown">
              <LanguageSwitcher variant="dropdown" />
            </Item>
          </Row>
        </Section>

        {/* ── RepeatableFormSection ── */}
        <Section title="Molecules · RepeatableFormSection">
          <Row title="Generic repeatable entries" col>
            <RepeatableFormSection
              entries={repeatableEntries}
              title={(i) => `Item ${i + 1}`}
              onAdd={addRepeatable}
              onRemove={removeRepeatable}
              addLabel="Add another item"
              renderFields={(entry, i) => (
                <div className="w-full flex-1">
                  <Input
                    label="Label"
                    placeholder="Enter a value"
                    value={entry.label}
                    onChange={(e) => updateRepeatable(i, e.target.value)}
                  />
                </div>
              )}
            />
          </Row>
        </Section>

        <Section title="Design Tokens · Gradients">
          <Row title="All gradients" col>
            {(
              [
                { bg: 'bg-gradient-indigo-coral', label: 'Indigo → Coral' },
                { bg: 'bg-gradient-indigo-white', label: 'Indigo → White' },
                { bg: 'bg-gradient-coral-white', label: 'Coral → White' },
                {
                  bg: 'bg-gradient-indigo-dark',
                  label: 'Indigo → Coral → Dark',
                },
                { bg: 'bg-gradient-indigo-gray', label: 'Indigo → Gray' },
                { bg: 'bg-gradient-coral-gray', label: 'Coral → Gray' },
              ] as const
            ).map(({ bg, label }) => (
              <div key={bg} className="w-full">
                <div className="mt-2 flex items-baseline gap-3">
                  <Span className="font-semibold text-neutral-900">
                    {label}
                  </Span>
                  <code className="text-xs text-neutral-300">{bg}</code>
                </div>
                <div className={`${bg} h-14 w-full rounded-xl`} />
              </div>
            ))}
          </Row>
        </Section>

        {/* ── Icons ── */}
        {(
          [
            { title: 'Coverage Feature', icons: CoverageFeatureIcons },
            { title: 'Arrow', icons: ArrowIcons },
            { title: 'Calendar', icons: CalendarIcons },
            { title: 'Communication', icons: CommunicationIcons },
            { title: 'Edit', icons: EditIcons },
            { title: 'Environment', icons: EnvironmentIcons },
            { title: 'File', icons: FileIcons },
            { title: 'Interface', icons: InterfaceIcons },
            { title: 'Media', icons: MediaIcons },
            { title: 'Menu', icons: MenuIcons },
            { title: 'Navigation', icons: NavigationIcons },
            { title: 'Shape', icons: ShapeIcons },
            { title: 'System', icons: SystemIcons },
            { title: 'User', icons: UserIcons },
            { title: 'Warning', icons: WarningIcons },
          ] as const
        ).map(({ title, icons }) => (
          <Section key={title} title={`Atoms · Icons · ${title}`}>
            <Row title="All">
              {(
                Object.entries(icons) as Array<
                  [string, React.FC<{ className?: string }>]
                >
              ).map(([name, Icon]) => (
                <Item key={name} label={name}>
                  <Icon className="h-6 w-6" />
                </Item>
              ))}
            </Row>
          </Section>
        ))}
      </div>
    </div>
  )
}
